import React from 'react';
import {
  CalculatorInputsContainer,
  InputContainer,
  EstimationContainer,
  EstimationValue,
  EstimationUnit,
  GenericCalculatorWrapper,
  GenericCalculatorHeader,
  PopoverTitle,
  PopoverBody,
  EstimationValueContainer,
  ResultIcon,
  InputTitle,
  CalculatorHelperLink,
  EstimationWrapper,
  SubTitle,
} from './styles';
import { ReactComponent as SuccessIcon } from '../../assets/svg/success.svg';
import { ReactComponent as SodimacErrorIcon } from '../../assets/svg/error-sodimac.svg';
import { ReactComponent as FalabellaErrorIcon } from '../../assets/svg/error-falabella.svg';
import { ReactComponent as SodimacInfoIcon } from '../../assets/svg/warning-sodimac.svg';
import { ReactComponent as FalabellaInfoIcon } from '../../assets/svg/warning-falabella.svg';
import Input from '../../components/Input';
import Popover from '../../components/Popover';
import { PopoverContainerWrapper } from '../../components/Popover/styles';
import {
  CUSTOM_REGEX,
  ERROR_TEXT,
  ESTIMATION_CONTAINER_TITLE,
  inputVarients,
  INPUT_TYPES,
  THEMES,
  TOOL_TIP,
} from './utils/constants';
import { Attributes, CalculatorConfig, Themes } from './models';
import InputSpinner from '../../components/InputSpinner';
import { useCalculator } from '../../hooks/useCalculator';
import { themes } from '../../themes';
import { StyledProvider } from '../../Layout/styledProvider';
import { paintCalculatorData } from './__mocks__/paintCalculatorData';
import get from 'lodash/get';
import { isNotPaintCalculator, parseAttributes } from './utils';

export type GenericCalculatorProps = {
  calculatorConfig: CalculatorConfig;
  handleChangeProductCounter?: (val: number) => void;
  productLevelAttributes: Attributes[];
  variantLevelAttributes: Attributes[];
  theme: Themes;
};

export const defaultProps: GenericCalculatorProps = paintCalculatorData as GenericCalculatorProps;

export const GenericCalculator: React.FC<GenericCalculatorProps> = ({
  calculatorConfig,
  handleChangeProductCounter,
  productLevelAttributes,
  variantLevelAttributes,
  theme,
}) => {
  let parsedProductLevelAttributes = [...productLevelAttributes];
  let parsedVariantLevelAttributes = [...variantLevelAttributes];
  if (theme === THEMES.FALABELLA) {
    parsedProductLevelAttributes = parseAttributes(productLevelAttributes);
    parsedVariantLevelAttributes = parseAttributes(variantLevelAttributes);
  }
  const {
    uiInputs,
    result,
    calculatorTitle,
    calculatorHelper,
    outputUnit,
    outputLogo,
    hideCalculator,
    tooltipPosition,
    extraSpaceNeededForToolTip,
    subTitle,
    updatePopOverValue,
    updateUiInputValue,
    handleInputOnBlur,
  } = useCalculator({
    calculatorConfig,
    productLevelAttributes: parsedProductLevelAttributes,
    variantLevelAttributes: parsedVariantLevelAttributes,
    handleChangeProductCounter,
  });

  if (hideCalculator) {
    return null;
  }

  const isSodimacTheme = theme === THEMES.SODIMAC;
  const selectedTheme = themes.filter((item: any) => item.id === theme).pop();
  const InfoIcon = isSodimacTheme ? SodimacInfoIcon : FalabellaInfoIcon;
  const ErrorIcon = isSodimacTheme ? SodimacErrorIcon : FalabellaErrorIcon;

  const getIcon = (value: string, isError: boolean) => {
    if (value === '' && isError) {
      return <ErrorIcon data-testid="error-icon" />;
    } else if (value != '' && !isError) {
      return <SuccessIcon data-testid="success-icon" />;
    } else {
      return <></>;
    }
  };

  const getInput = ({ inputData, inputValue, isError, errorText, type }) => {
    const { placeholder_value: placeHolderValue } = inputData;
    const placeHolder_default = placeHolderValue ? placeHolderValue : 'Ej. 30';
    return (
      <>
        {type && Object.keys(INPUT_TYPES).includes(type) ? (
          <Input
            value={inputValue}
            onChange={(newValue) => updateUiInputValue(newValue, inputData)}
            type={INPUT_TYPES[type]}
            icon={getIcon(inputValue, isError)}
            error={isError}
            errorText={errorText}
            greenOutlineOnSuccess={true}
            onBlur={(event) => handleInputOnBlur(event, inputData)}
            placeholder={placeHolder_default}
            style={{ width: '100%' }}
            variant={inputVarients[theme]}
            customRegex={CUSTOM_REGEX[type]}
          />
        ) : null}
        {type && type === 'Counter' ? (
          <InputSpinner
            value={inputValue || 1}
            onChange={(newValue) => updateUiInputValue(newValue, inputData)}
            min={1}
            size={theme === THEMES.FALABELLA ? 'sm' : 'md'}
          />
        ) : null}
      </>
    );
  };

  const getToolTip = ({ toolTip, showPopover, inputData }) => {
    const { SIZE, BACKGROUND_COLOR } = TOOL_TIP;
    const title = get(toolTip, 'title', '').trim();
    const description = get(toolTip, 'description', '').trim();
    const showTooltip = !!title || !!description;
    return (
      <>
        {showTooltip ? (
          <PopoverContainerWrapper>
            <Popover
              visible={showPopover}
              size={SIZE}
              backgroundColor={BACKGROUND_COLOR}
              place={tooltipPosition}
              correctTooltipPosition={extraSpaceNeededForToolTip}
              data-testid="popover"
            >
              {!!title ? <PopoverTitle>{title}</PopoverTitle> : null}
              {!!description ? <PopoverBody>{description}</PopoverBody> : null}
            </Popover>
            <InfoIcon onClick={(event) => updatePopOverValue(event, !showPopover, inputData)} data-testid="info-icon" />
          </PopoverContainerWrapper>
        ) : null}
      </>
    );
  };

  const getInputTitle = ({ title, toolTip, showPopover, inputData }) => {
    return (
      <>
        <InputTitle>
          <label>{title}</label>
          {getToolTip({ toolTip, showPopover, inputData })}
        </InputTitle>
      </>
    );
  };

  const getHelperText = ({ shortName, fieldShortName, file, isError, helperTitle }) => {
    return (
      <>
        {shortName === fieldShortName && !!file.trim() ? (
          <CalculatorHelperLink href={file} target="_blank" status={isError ? 'error' : 'success'}>
            {helperTitle}
          </CalculatorHelperLink>
        ) : null}
      </>
    );
  };

  const getCalculatorInputs = () => {
    const isNotPaintCal = isNotPaintCalculator(uiInputs);
    const isFullWidth = theme === THEMES.FALABELLA && isNotPaintCal;
    return uiInputs.map((inputData) => {
      const {
        title,
        tooltip: toolTip,
        input_type: inputType,
        short_name: shortName,
        isError,
        showPopover,
        error_text,
      } = inputData;
      const errorText = error_text ? error_text : ERROR_TEXT;

      const { field_short_name: fieldShortName, title: helperTitle, file } = calculatorHelper;
      const inputValue: any = inputData.value || '';
      return (
        <InputContainer key={title} isFullWidth={isFullWidth}>
          {getInputTitle({ title, toolTip, showPopover, inputData })}
          {getInput({ inputData, isError, errorText: errorText, type: inputType, inputValue })}
          {getHelperText({ shortName, fieldShortName, file, isError, helperTitle })}
        </InputContainer>
      );
    });
  };

  return (
    <>
      {!hideCalculator ? (
        <StyledProvider theme={selectedTheme}>
          <GenericCalculatorWrapper>
            <GenericCalculatorHeader>{calculatorTitle}</GenericCalculatorHeader>
            <CalculatorInputsContainer>
              {getCalculatorInputs()}
              <EstimationWrapper>
                <EstimationContainer>
                  <label>{ESTIMATION_CONTAINER_TITLE}</label>
                  <EstimationValueContainer>
                    <EstimationValue>{result.quantityRequired}</EstimationValue>
                    <EstimationUnit>{outputUnit}</EstimationUnit>
                    <ResultIcon>
                      <img src={outputLogo} alt="unit-logo" />
                    </ResultIcon>
                  </EstimationValueContainer>
                </EstimationContainer>
              </EstimationWrapper>
            </CalculatorInputsContainer>
            {subTitle && subTitle.length > 0 ? <SubTitle>{subTitle}</SubTitle> : null}
          </GenericCalculatorWrapper>
        </StyledProvider>
      ) : null}
    </>
  );
};
